<!-- src/views/admin/AdminEngineeringDashboardView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">

        <!-- 1. HEADER & INTEGRATED COMMAND FILTERS -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl mb-10 relative z-30 text-left border-x border-t border-slate-800">
          <div class="absolute top-0 right-0 p-12 opacity-5"><WrenchScrewdriverIcon class="h-32 w-32 text-white" /></div>
          
          <div class="relative z-10">
            <div class="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-8 mb-8">
              <div>
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Projects Command Centre</h1>
                <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4">Field Intelligence • Resource Oversight & Site Registry</p>
              </div>
              
              <div class="flex flex-wrap items-center gap-3">
                <!-- CONTEXTUAL ACTION BUTTONS -->
                <button v-if="activeTab === 'templates'" @click="openCreateModal" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2 border-none cursor-pointer">
                    <PlusIcon class="h-4 w-4" /> <span>Create Template</span>
                </button>

                <router-link v-if="activeTab === 'projects'" :to="{ name: 'CreateProject' }" class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2">
                    <PlusIcon class="h-4 w-4" /> <span>Initialise Project</span>
                </router-link>

                <!-- 🛡️ CLIENT FEEDBACK QR MODAL TRIGGER (Appears when Client Feedback tab is active) -->
                <button 
                  v-if="activeTab === 'clientFeedback'" 
                  @click="openGlobalClientFeedbackModal" 
                  class="px-6 py-3.5 bg-[#20FCF1] text-[#0A1630] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2"
                >
                  <QrCodeIcon class="h-4 w-4 text-[#0A1630]" /> <span>Client Feedback QR</span>
                </button>

                <!-- 🛡️ VISIBILITY RESTRICTED: Display only on Field Support Registry tab -->
                <router-link v-if="canManageOptions && activeTab === 'submissions'" to="/admin/engineering/manage-options" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2">
                    <AdjustmentsHorizontalIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Manage Options</span>
                </router-link>

                <!-- 🛡️ VISIBILITY RESTRICTED: Display only on Field Support Registry tab -->
                <router-link v-if="canManageOptions && activeTab === 'submissions'" to="/admin/engineering/manage-text" class="px-6 py-3.5 bg-white/10 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/20 transition-all flex items-center gap-2 shadow-lg">
                    <DocumentTextIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Manage Text</span>
                </router-link>

                <!-- 🛡️ NAVIGATION STANDARDISED: Back button label updated from 'Admin' to 'Return' -->
                <router-link to="/client-admin" class="px-6 py-3.5 bg-white/5 border border-white/10 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
                  <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return</span>
                </router-link>
              </div>
            </div>

            <!-- INTEGRATED FILTER ROW -->
            <div class="flex flex-col lg:flex-row lg:items-end justify-between gap-6 bg-white/5 p-6 rounded-[32px] border border-white/10">
              <div class="flex flex-wrap gap-4 items-center">
                <div class="flex flex-col gap-1.5 min-w-[240px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Search Registry</span>
                  <input v-model="filters.search" type="text" placeholder="Reference, Project or Client..." class="filter-input-dark" />
                </div>

                <!-- MASTER REGISTRY SELECTOR WITH TOTAL COUNTS -->
                <div class="flex flex-col gap-1.5 min-w-[300px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Registry Mode Selection</span>
                  <select v-model="activeTab" class="filter-select-dark">
                    <option value="submissions">Field Support Registry ({{ engineeringTabCounts.submissions }})</option>
                    <option value="projects">Project Management ({{ engineeringTabCounts.projects }})</option>
                    <option value="templates">Support Request Templates ({{ engineeringTabCounts.templates }})</option>
                    <option value="clientFeedback">Client Feedback Questionnaires ({{ engineeringTabCounts.clientFeedback }})</option>
                    <option value="singleItemInspection">Single-Item Plant Inspections ({{ engineeringTabCounts.singleItemInspection }})</option>
                    <option value="multiItemInspection">Multi-Item Shift Checks ({{ engineeringTabCounts.multiItemInspection }})</option>
                    <option value="worksiteInspections">Worksite Inspections ({{ engineeringTabCounts.worksiteInspections }})</option>
                  </select>
                </div>

                <div class="flex flex-col gap-1.5 min-w-[160px]">
                  <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-2">Status / Rating Filter</span>
                  <select v-model="filters.status" class="filter-select-dark">
                    <option value="all">All Statuses</option>
                    <template v-if="activeTab === 'submissions' || activeTab === 'singleItemInspection' || activeTab === 'multiItemInspection' || activeTab === 'worksiteInspections'">
                      <option value="Pending Review">Pending Review</option>
                      <option value="Reviewed">Reviewed</option>
                      <option value="Requires Action">Requires Action</option>
                    </template>
                    <template v-if="activeTab === 'projects'">
                      <option value="Active">Active</option>
                      <option value="Planning">Planning</option>
                      <option value="Complete">Complete</option>
                    </template>
                    <template v-if="activeTab === 'clientFeedback'">
                      <option value="all">All Ratings</option>
                      <option value="5">5 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="3">3 Stars</option>
                    </template>
                  </select>
                </div>
              </div>

              <button 
                @click="exportToExcel"
                :disabled="exporting || currentViewData.length === 0"
                class="px-8 py-4 bg-white/10 border border-white/20 text-white rounded-2xl font-black uppercase text-[10px] tracking-[0.2em] hover:bg-white/20 transition-all flex items-center gap-3 disabled:opacity-50 shadow-xl"
              >
                <template v-if="exporting">
                  <div class="animate-spin h-3 w-3 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
                  <span>Syncing...</span>
                </template>
                <template v-else>
                  <TableCellsIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Export Current List</span>
                </template>
              </button>
            </div>
          </div>
        </div>

        <!-- 2. SENTINEL INTELLIGENCE & INTEGRATED METRICS HUB -->
        <div class="mb-12 animate-fadeIn relative z-10 text-left">
          <div class="bg-white border-2 border-slate-100 rounded-[40px] shadow-2xl overflow-hidden relative text-left">
            <div class="bg-[#0A1630] px-10 py-6 border-b-4 border-[#20FCF1] flex flex-col md:flex-row justify-between items-center gap-6">
               <div class="flex items-center gap-4 text-left">
                  <div class="bg-[#20FCF1]/10 p-3 rounded-2xl">
                    <SparklesIcon class="h-6 w-6 text-[#20FCF1]" />
                  </div>
                  <div>
                    <h2 class="text-xl font-black text-white uppercase tracking-tight leading-none text-left">Sentinel Intelligence</h2>
                    <p class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.3em] mt-2 text-left">Active Project Synthesis • Deterministic Audit Logic</p>
                  </div>
               </div>
               
               <button 
                 @click="triggerSynthesis" 
                 :disabled="isAiLoading"
                 class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg"
               >
                 <span v-if="isAiLoading" class="animate-spin h-3 w-3 border-2 border-[#0A1630] border-t-transparent rounded-full"></span>
                 <SignalIcon v-else class="h-4 w-4" />
                 <span>{{ isAiLoading ? 'Synthesising...' : 'Synchronise Intelligence' }}</span>
               </button>
            </div>

            <div class="p-8 md:p-12 text-left">
               <!-- 📊 INTEGRATED METRICS ROW inside Sentinel Intelligence Card -->
               <div class="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 border-b border-slate-100 pb-8 text-left">
                 <div v-for="stat in engineeringStats" :key="stat.label" class="flex items-center gap-4 text-left">
                   <div class="relative w-12 h-12 flex items-center justify-center text-center">
                     <svg class="absolute inset-0 w-full h-full" viewBox="0 0 24 24" fill="none">
                         <path d="M12 2L3 7V12C3 17.5 7.1 21.6 12 23C16.9 21.6 21 17.5 21 12V7L12 2Z" :fill="stat.color" fill-opacity="0.1" :stroke="stat.color" stroke-width="1.5"/>
                     </svg>
                     <component :is="stat.icon" class="w-5 h-5" :style="{ color: stat.color }" />
                   </div>
                   <div class="text-left">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 text-left">{{ stat.label }}</p>
                     <h4 class="text-xl font-black text-[#0A1630] leading-none text-left">{{ stat.value }}</h4>
                   </div>
                 </div>
               </div>

               <!-- Initial State -->
               <div v-if="!aiReport && !isAiLoading" class="flex flex-col items-center justify-center py-12 text-center">
                  <div class="bg-slate-50 p-6 rounded-full mb-6">
                    <CpuChipIcon class="h-10 w-10 text-slate-200" />
                  </div>
                  <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">Awaiting Initialisation</h3>
                  <p class="text-xs text-slate-400 font-bold mt-2 max-w-sm">Trigger a synchronisation to audit current project tasks, project statuses, and field deployments.</p>
               </div>

               <!-- 🛡️ LOCAL SYNTHESIS CONTENT -->
               <div v-else-if="aiReport" class="grid grid-cols-1 lg:grid-cols-3 gap-10">
                  <div class="lg:col-span-2 space-y-8">
                     <div class="border-l-4 border-[#20FCF1] pl-6">
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-3">Executive Summary</h4>
                        <p class="text-sm font-medium text-slate-600 leading-relaxed italic">
                          "{{ aiReport.summary }}"
                        </p>
                     </div>

                     <div>
                        <h4 class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-4">Priority Interventions</h4>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                           <div v-for="(action, idx) in aiReport.actions" :key="idx" class="bg-slate-50 p-5 rounded-3xl border border-slate-100 flex flex-col justify-between">
                              <div>
                                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-widest block mb-2">{{ action.type }}</span>
                                <p class="text-xs font-black text-[#0A1630] leading-tight mb-3 uppercase tracking-tight">{{ action.instruction }}</p>
                              </div>
                              <p class="text-[10px] text-slate-500 font-medium leading-relaxed">{{ action.reason }}</p>
                           </div>
                        </div>
                     </div>
                  </div>

                  <div class="bg-[#0A1630]/5 p-8 rounded-[32px] border border-slate-100 h-fit">
                    <h4 class="text-[10px] font-black text-[#0A1630] uppercase tracking-[0.3em] mb-6 border-b border-[#0A1630]/10 pb-4">Task Hotspots</h4>
                    <div class="space-y-6">
                       <div v-for="(trend, idx) in aiReport.trends" :key="idx" class="flex items-start gap-4">
                          <div :class="trend.isCritical ? 'bg-rose-500' : 'bg-[#20FCF1]'" class="h-2 w-2 rounded-full shrink-0 mt-1.5 shadow-sm"></div>
                          <div>
                            <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ trend.label }}</p>
                            <p class="text-[11px] text-slate-500 font-medium mt-1 leading-tight">{{ trend.value }}</p>
                          </div>
                       </div>
                    </div>
                    <div class="mt-8 pt-6 border-t border-[#0A1630]/10 text-[9px] font-black text-slate-400 uppercase">
                      Analysis Depth: Active Task Profiles
                    </div>
                  </div>
               </div>

               <!-- Loading State -->
               <div v-if="isAiLoading" class="flex flex-col items-center justify-center py-12">
                  <div class="animate-spin h-12 w-12 border-4 border-[#20FCF1] border-t-transparent rounded-full mb-4"></div>
                  <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Aggregating Cross-Project Data & Synthesising Findings...</p>
               </div>
            </div>
          </div>
        </div>

        <!-- 4. DATA DISPLAY AREA -->
        <div v-if="loading" class="text-center py-24">
          <div class="animate-spin h-10 w-10 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-4"></div>
          <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Initialising Project Registry...</p>
        </div>

        <div v-else-if="paginatedViewData.length === 0" class="text-center py-24 bg-slate-50 rounded-[40px] border border-dashed border-slate-200 animate-fadeIn shadow-inner">
           <MagnifyingGlassIcon class="h-14 w-14 text-slate-200 mx-auto mb-6 opacity-30 text-center" />
           <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">Registry Neutral</h3>
           <p class="text-xs font-bold text-slate-400 uppercase tracking-widest mt-2">No records identified for the selected mode.</p>
        </div>

        <div v-else class="space-y-4 animate-fadeIn text-left">
          <!-- MODE: SUBMISSIONS -->
          <template v-if="activeTab === 'submissions'">
            <div v-for="sub in paginatedViewData" :key="sub.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
                <div class="flex items-center gap-6 w-full xl:w-[25%] shrink-0">
                  <div class="relative h-20 w-20 shrink-0 bg-white p-1 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                    <img v-if="qrCodeUrls[sub.id]" :src="qrCodeUrls[sub.id]" alt="QR" class="w-full h-full object-contain" />
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight leading-none">{{ sub.humanReadableId || sub.formData?.humanReadableId }}</h3>
                  </div>
                </div>
                <div class="flex-grow px-4 border-l border-slate-50 min-w-0 flex items-baseline gap-4">
                  <!-- IDENTITY SEAL: Resolves the Guest mapping issue -->
                  <h4 class="text-sm font-black text-[#0A1630] uppercase leading-none">{{ sub.userName }}</h4>
                  <span class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ formatDate(sub.submittedAt) }}</span>
                </div>
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                   <span :class="statusClass(sub.status)" class="px-5 py-2 text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">
                      {{ sub.status || 'Pending' }}
                   </span>
                   <router-link :to="{ name: 'completedForm', params: { collectionName: 'supportRequestSubmissions', submissionId: sub.id } }" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">
                     <EyeIcon class="h-4 w-4" /> <span>Review Full Audit</span>
                   </router-link>
                </div>
              </div>
            </div>
          </template>

          <!-- MODE: PROJECTS -->
          <template v-if="activeTab === 'projects'">
            <div v-for="project in paginatedViewData" :key="project.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group">
               <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
                <div class="flex items-center gap-6 w-full xl:w-[25%] shrink-0">
                  <div class="relative h-20 w-20 shrink-0 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex items-center justify-center">
                    <img v-if="project.imageUrl" :src="project.imageUrl" class="w-full h-full object-cover" alt="site" />
                    <BriefcaseIcon v-else class="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight truncate">{{ project.name }}</h3>
                </div>
                <div class="flex-grow px-4 border-l border-slate-50 min-w-0">
                  <p class="text-[10px] font-black text-slate-300 uppercase tracking-[0.2em] mb-1">Site Location</p>
                  <p class="text-sm font-bold text-slate-600 uppercase">{{ project.location || 'Not Defined' }}</p>
                </div>
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-3">
                   <span :class="getStatusClass(project.status)" class="px-5 py-2 text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">{{ project.status }}</span>
                   <router-link :to="{ name: 'ProjectDetails', params: { id: project.id }}" class="action-btn hover:bg-[#0A1630] hover:text-white"><EyeIcon class="h-4 w-4" /></router-link>
                   <router-link :to="{ name: 'EditProject', params: { id: project.id }}" class="action-btn hover:bg-[#20FCF1] hover:text-[#0A1630]"><PencilSquareIcon class="h-4 w-4" /></router-link>
                   <button @click="deleteProject(project)" class="action-btn hover:bg-rose-500 hover:text-white"><TrashIcon class="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          </template>

          <!-- MODE: CLIENT FEEDBACK QUESTIONNAIRES -->
          <template v-if="activeTab === 'clientFeedback'">
            <div v-for="cfq in paginatedViewData" :key="cfq.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-emerald-400/30 transition-all p-5 md:p-6 group">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
                <div class="flex items-center gap-6 w-full xl:w-[28%] shrink-0">
                  <div class="relative h-16 w-16 shrink-0 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center justify-center text-emerald-600 font-black">
                    <StarIcon class="w-8 h-8 fill-amber-400 text-amber-400" />
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-none">{{ cfq.humanReadableId || cfq.formData?.humanReadableId || 'SS-CFQ' }}</h3>
                    <span class="text-[9px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-md mt-2 inline-block">Client Closeout</span>
                  </div>
                </div>
                <div class="flex-grow px-4 border-l border-slate-50 min-w-0 space-y-1">
                  <div class="flex items-center gap-3">
                    <h4 class="text-sm font-black text-[#0A1630] uppercase">{{ cfq.formData?.projectName || cfq.projectName || 'Project Review' }}</h4>
                    <span class="text-[10px] font-bold text-slate-400">|</span>
                    <span class="text-xs font-bold text-slate-600 uppercase">{{ cfq.formData?.clientName || cfq.clientName || 'Client Representative' }}</span>
                  </div>
                  <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Submitted: {{ formatDate(cfq.submittedAt) }}</p>
                </div>
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                   <div class="flex items-center gap-1.5 bg-amber-50 px-4 py-2 rounded-2xl border border-amber-100">
                      <StarIcon class="h-4 w-4 text-amber-400 fill-amber-400" />
                      <span class="text-xs font-black text-[#0A1630]">{{ cfq.formData?.overallRating || cfq.overallRating || '5.0' }} / 5</span>
                   </div>
                   <router-link :to="{ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: cfq.id } }" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">
                     <EyeIcon class="h-4 w-4" /> <span>Review Feedback</span>
                   </router-link>
                </div>
              </div>
            </div>
          </template>

          <!-- MODE: SINGLE ITEM PLANT INSPECTIONS (SS-PEI-1) -->
          <template v-if="activeTab === 'singleItemInspection'">
            <div v-for="insp in paginatedViewData" :key="insp.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
                <div class="flex items-center gap-6 w-full xl:w-[28%] shrink-0">
                  <div class="h-16 w-16 shrink-0 bg-[#0A1630] text-[#20FCF1] rounded-2xl flex items-center justify-center font-black text-xs">
                    {{ insp.itemDetails?.assetId || 'ASSET' }}
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight truncate">{{ insp.itemDetails?.description || 'Plant Inspection' }}</h3>
                    <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded mt-1 inline-block">Protocol SS-PEI-1</span>
                  </div>
                </div>
                <div class="flex-grow px-4 border-l border-slate-50 min-w-0 space-y-1">
                  <h4 class="text-sm font-black text-[#0A1630] uppercase">Site: {{ insp.itemDetails?.siteLocation || 'N/A' }} | Inspector: {{ insp.signOff?.inspectorName || insp.userName }}</h4>
                  <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Week Commencing: {{ insp.itemDetails?.weekCommencing || formatDate(insp.submittedAt) }}</p>
                </div>
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                   <span :class="statusClass(insp.status)" class="px-5 py-2 text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">
                      {{ insp.status || 'Pending Review' }}
                   </span>
                   <router-link :to="{ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: insp.id } }" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">
                     <EyeIcon class="h-4 w-4" /> <span>Review Full Audit</span>
                   </router-link>
                </div>
              </div>
            </div>
          </template>

          <!-- MODE: MULTI ITEM SHIFT CHECKS (SS-PEI-2) -->
          <template v-if="activeTab === 'multiItemInspection'">
            <div v-for="insp in paginatedViewData" :key="insp.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
                <div class="flex items-center gap-6 w-full xl:w-[28%] shrink-0">
                  <div class="h-16 w-16 shrink-0 bg-[#0A1630] text-[#20FCF1] rounded-2xl flex items-center justify-center font-black text-xs">
                    MULTI
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight truncate">{{ insp.siteDetails?.siteLocation || 'Shift Check' }}</h3>
                    <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded mt-1 inline-block">Protocol SS-PEI-2</span>
                  </div>
                </div>
                <div class="flex-grow px-4 border-l border-slate-50 min-w-0 space-y-1">
                  <h4 class="text-sm font-black text-[#0A1630] uppercase">Shift: {{ insp.siteDetails?.shiftTime || 'N/A' }} | Inspector: {{ insp.signOff?.inspectorName || insp.userName }}</h4>
                  <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date: {{ insp.siteDetails?.date || formatDate(insp.submittedAt) }}</p>
                </div>
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                   <span :class="statusClass(insp.status)" class="px-5 py-2 text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">
                      {{ insp.status || 'Pending Review' }}
                   </span>
                   <router-link :to="{ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: insp.id } }" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">
                     <EyeIcon class="h-4 w-4" /> <span>Review Full Audit</span>
                   </router-link>
                </div>
              </div>
            </div>
          </template>

          <!-- MODE: WORKSITE INSPECTIONS -->
          <template v-if="activeTab === 'worksiteInspections'">
            <div v-for="insp in paginatedViewData" :key="insp.id" class="bg-white border border-slate-100 rounded-[32px] shadow-sm hover:shadow-xl hover:border-[#20FCF1]/30 transition-all p-5 md:p-6 group">
              <div class="flex flex-col xl:flex-row items-center justify-between gap-6">
                <div class="flex items-center gap-6 w-full xl:w-[28%] shrink-0">
                  <div class="h-16 w-16 shrink-0 bg-[#0A1630] text-[#20FCF1] rounded-2xl flex items-center justify-center font-black text-xs">
                    SITE
                  </div>
                  <div class="min-w-0">
                    <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight truncate">{{ insp.formData?.siteName || insp.siteName || 'Worksite Inspection' }}</h3>
                    <span class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest bg-slate-50 px-2 py-0.5 rounded mt-1 inline-block">Protocol MSP014.03</span>
                  </div>
                </div>
                <div class="flex-grow px-4 border-l border-slate-50 min-w-0 space-y-1">
                  <h4 class="text-sm font-black text-[#0A1630] uppercase">Site Access: {{ insp.formData?.siteAccess || insp.siteAccess || 'N/A' }} | Inspector: {{ insp.formData?.inspectorName || insp.inspectorName || insp.userName }}</h4>
                  <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">Date: {{ formatDate(insp.submittedAt) }}</p>
                </div>
                <div class="w-full xl:w-auto shrink-0 flex items-center justify-end gap-6">
                   <span :class="statusClass(insp.status)" class="px-5 py-2 text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">
                      {{ insp.status || 'Pending Review' }}
                   </span>
                   <router-link :to="{ name: 'completedForm', params: { collectionName: 'healthsafetyFormSubmissions', submissionId: insp.id } }" class="px-8 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2">
                     <EyeIcon class="h-4 w-4" /> <span>Review Full Audit</span>
                   </router-link>
                </div>
              </div>
            </div>
          </template>

          <!-- MODE: TEMPLATES -->
          <template v-if="activeTab === 'templates'">
             <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
               <div v-for="form in paginatedViewData" :key="form.id" class="p-8 bg-white rounded-[40px] border border-slate-100 flex items-center justify-between group hover:border-[#20FCF1] transition-all shadow-sm">
                <div class="flex items-center gap-6">
                   <div class="p-5 bg-slate-50 rounded-3xl text-slate-400 group-hover:bg-[#0A1630] group-hover:text-white transition-all duration-500">
                     <QrCodeIcon class="h-8 w-8" />
                   </div>
                   <div>
                      <a :href="'/support-request/' + form.id" target="_blank" class="text-lg font-black text-[#0A1630] uppercase tracking-tight hover:text-[#FD5E00] transition-colors leading-none">
                        {{ form.companyDisplayName }}
                      </a>
                      <p class="text-[9px] font-bold text-slate-300 tracking-[0.2em] mt-3 uppercase">PROTOCOL: {{ form.id }}</p>
                   </div>
                </div>
                <div class="flex flex-col gap-2">
                  <button @click="shareForm(form)" class="px-5 py-2.5 rounded-xl bg-[#0A1630] text-[#20FCF1] text-[9px] font-black uppercase tracking-widest hover:brightness-110 transition-all cursor-pointer border-none shadow-sm">
                    {{ copiedSlug === form.id ? 'Synchronised' : 'Share URL' }}
                  </button>
                  <div class="flex gap-2">
                    <button @click="openEditModal(form)" class="flex-1 p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-[#0A1630] hover:text-white transition-all border-none cursor-pointer"><PencilSquareIcon class="h-4 w-4"/></button>
                    <button @click="handleDelete(form)" class="flex-1 p-2.5 rounded-xl bg-slate-50 text-slate-400 hover:bg-rose-500 hover:text-white transition-all border-none cursor-pointer"><TrashIcon class="h-4 w-4"/></button>
                  </div>
                </div>
               </div>
             </div>
          </template>

          <!-- Symmetrical Pagination Controls (Forced h-11 heights) -->
          <div v-if="totalPages > 1 || itemsPerPage !== 25" class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12 no-print">
            <div class="flex items-center gap-4">
              <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                &larr; Prev
              </button>
              <span class="text-xs font-black text-[#0A1630] uppercase tracking-widest">
                Page {{ currentPage }} of {{ totalPages || 1 }}
              </span>
              <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages || totalPages === 0"
                class="h-11 flex items-center justify-center px-6 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 disabled:opacity-30 disabled:hover:bg-[#0A1630] transition-all cursor-pointer shadow-md border-none"
              >
                Next &rarr;
              </button>
            </div>
            
            <!-- Symmetrical Items Per Page Selector (Forced h-11 height) -->
            <div class="flex items-center gap-3">
              <span class="text-[9px] font-black text-[#0A1630] uppercase tracking-widest">Display:</span>
              <select 
                v-model="itemsPerPage" 
                class="h-11 bg-[#0A1630] text-white rounded-xl px-5 text-[10px] font-black uppercase tracking-widest outline-none cursor-pointer shadow-md appearance-none pr-8 text-center border-none"
                style="background-image: url('data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2320FCF1\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E'); background-repeat: no-repeat; background-position: right 0.75rem center; background-size: 0.75rem;"
              >
                <option :value="25">25 Rows</option>
                <option :value="50">50 Rows</option>
                <option :value="100">100 Rows</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 🛡️ GLOBAL CLIENT FEEDBACK QR MODAL -->
    <div v-if="isClientFeedbackModalOpen" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div class="bg-white rounded-[40px] max-w-xl w-full p-8 md:p-10 shadow-2xl border border-slate-100 animate-fadeIn text-left space-y-8">
        <div class="flex justify-between items-center border-b pb-6">
          <div class="flex items-center gap-3">
            <div class="bg-[#0A1630] p-2 rounded-xl shadow-lg border-b-2 border-emerald-400">
              <QrCodeIcon class="h-5 w-5 text-emerald-400" />
            </div>
            <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">Client Feedback Questionnaire</h3>
          </div>
          <button @click="isClientFeedbackModalOpen = false" class="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2.5 rounded-xl transition-all">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>

        <div class="flex flex-col items-center gap-6">
          <div class="bg-[#0A1630] p-4 rounded-[28px] shadow-xl border-4 border-emerald-400/20">
            <div class="bg-white p-3 rounded-2xl">
              <img v-if="globalClientFeedbackQrUrl" :src="globalClientFeedbackQrUrl" alt="Client Feedback QR" class="w-48 h-48" />
            </div>
          </div>
          <span class="text-[8px] font-black text-emerald-600 uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-md border border-emerald-100">
            General Company QR Protocol
          </span>
        </div>

        <p class="text-xs text-slate-500 font-medium leading-relaxed text-center">
          Present this QR code or share the link with client representatives to evaluate our project service ratings and site performance.
        </p>

        <div class="flex flex-col gap-3">
          <button @click="shareGlobalFeedbackEmail" class="w-full bg-[#0A1630] text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-md">
            <EnvelopeIcon class="h-4 w-4 text-emerald-400" /> <span>Email Questionnaire Link</span>
          </button>
          <button @click="copyGlobalFeedbackLink" class="w-full bg-white border border-slate-200 text-[#0A1630] px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all flex items-center justify-center gap-2 shadow-sm">
            <ClipboardIcon class="h-4 w-4 text-emerald-400" /> <span>{{ copiedGlobalFeedbackText }}</span>
          </button>
        </div>
      </div>
    </div>

    <CreateSupportRequestModal v-if="isModalOpen" :template="editingTemplate" @close="closeModal" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watchEffect, watch } from 'vue';
import { useRoute } from 'vue-router';
import { db, firebaseApp } from '@/firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, limit, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import CreateSupportRequestModal from '@/components/engineering/CreateSupportRequestModal.vue';
import { 
  WrenchScrewdriverIcon, ArrowUturnLeftIcon,
  AdjustmentsHorizontalIcon, EyeIcon, TableCellsIcon, MagnifyingGlassIcon,
  PlusIcon, QrCodeIcon, BriefcaseIcon, PencilSquareIcon, TrashIcon,
  ExclamationTriangleIcon, SparklesIcon, CpuChipIcon, SignalIcon, DocumentTextIcon,
  StarIcon, EnvelopeIcon, ClipboardIcon, XMarkIcon, ClipboardDocumentCheckIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const route = useRoute();
const functions = getFunctions(firebaseApp, 'europe-west2');

const loading = ref(true);
const exporting = ref(false);
const analyticsData = ref({});
const activeTab = ref('submissions');
const isModalOpen = ref(false);
const editingTemplate = ref(null);
const copiedSlug = ref(null);

const projects = ref([]);
const supportRequestTemplates = ref([]);
const supportRequestSubmissions = ref([]); 
const clientFeedbackSubmissions = ref([]); 
const singleItemInspectionSubmissions = ref([]); 
const multiItemInspectionSubmissions = ref([]);   
const worksiteInspectionSubmissions = ref([]); // 🛡️ WORKSITE INSPECTIONS REGISTRY
const qrCodeUrls = ref({});

// Global Client Feedback Modal state
const isClientFeedbackModalOpen = ref(false);
const globalClientFeedbackQrUrl = ref('');
const copiedGlobalFeedbackText = ref('Copy Questionnaire Link');

// PAGINATION STATE
const currentPage = ref(1);
const itemsPerPage = ref(25);

// 🛡️ SYNTHESIS STATE REGISTRY
const aiReport = ref(null);
const isAiLoading = ref(false);

const filters = reactive({
  search: '',
  status: 'all'
});

const rowsPerPage = 100;

/** 🛡️ BROWSER TAB SYNC: Resolves "Stuck Tab" issue in SPAs */
watchEffect(() => {
  const modeName = activeTab.value === 'submissions' ? 'Field Support' : 
                   activeTab.value === 'projects' ? 'Project Management' : 
                   activeTab.value === 'clientFeedback' ? 'Client Feedback Questionnaires' : 
                   activeTab.value === 'singleItemInspection' ? 'Single-Item Plant Inspections' :
                   activeTab.value === 'multiItemInspection' ? 'Multi-Item Shift Checks' :
                   activeTab.value === 'worksiteInspections' ? 'Worksite Inspections' : 'Support Request Templates';
  document.title = `${modeName} | Sentinel Safe Command Centre`;
});

const engineeringStats = computed(() => {
  return [
    { label: 'Total Requests', value: supportRequestSubmissions.value.length, color: '#6366F1', icon: WrenchScrewdriverIcon },
    { label: 'Pending Action', value: supportRequestSubmissions.value.filter(s => s.status === 'Pending').length, color: '#EF4444', icon: ExclamationTriangleIcon },
    { label: 'Active Projects', value: projects.value.filter(p => p.status === 'Active').length, color: '#20FCF1', icon: BriefcaseIcon },
    { label: 'Site Inspections', value: worksiteInspectionSubmissions.value.length, color: '#FBBF24', icon: ClipboardDocumentCheckIcon },
  ];
});

// 🛡️ ENGINEERING TAB COUNTS FOR DROPDOWN SELECTION
const engineeringTabCounts = computed(() => ({
  submissions: supportRequestSubmissions.value.length,
  projects: projects.value.length,
  templates: supportRequestTemplates.value.length,
  clientFeedback: clientFeedbackSubmissions.value.length,
  singleItemInspection: singleItemInspectionSubmissions.value.length,
  multiItemInspection: multiItemInspectionSubmissions.value.length,
  worksiteInspections: worksiteInspectionSubmissions.value.length
}));

const currentViewData = computed(() => {
  let source = [];
  if (activeTab.value === 'submissions') source = supportRequestSubmissions.value;
  else if (activeTab.value === 'projects') source = projects.value;
  else if (activeTab.value === 'templates') source = supportRequestTemplates.value;
  else if (activeTab.value === 'clientFeedback') source = clientFeedbackSubmissions.value;
  else if (activeTab.value === 'singleItemInspection') source = singleItemInspectionSubmissions.value;
  else if (activeTab.value === 'multiItemInspection') source = multiItemInspectionSubmissions.value;
  else if (activeTab.value === 'worksiteInspections') source = worksiteInspectionSubmissions.value;

  return source.filter(item => {
    const queryStr = (filters.search || '').toLowerCase().trim();
    let matchSearch = true;

    if (queryStr) {
      const searchWords = queryStr.split(/\s+/);
      const searchableText = [
        item.name,
        item.companyDisplayName,
        item.userName,
        item.humanReadableId,
        item.id,
        item.projectName,
        item.clientName,
        item.siteAddress,
        item.bridgeRef,
        item.lineRef,
        item.status,
        item.itemDetails?.assetId,
        item.itemDetails?.description,
        item.itemDetails?.siteLocation,
        item.siteDetails?.siteLocation,
        item.formData?.siteName,
        item.siteName,
        ...(item.formData ? Object.values(item.formData) : [])
      ].filter(Boolean).join(' ').toLowerCase();

      matchSearch = searchWords.some(word => searchableText.includes(word));
    }
    
    let matchStatus = true;
    if (filters.status !== 'all') {
      if (activeTab.value === 'clientFeedback') {
        const rating = String(item.formData?.overallRating || item.overallRating || '');
        matchStatus = rating.startsWith(filters.status);
      } else {
        matchStatus = (item.status || '').toLowerCase() === filters.status.toLowerCase();
      }
    }
    return matchSearch && matchStatus;
  });
});

// PAGINATION MATH & CONTROL PROTOCOLS
const totalPages = computed(() => {
  return Math.ceil(currentViewData.value.length / itemsPerPage.value);
});

const paginatedViewData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return currentViewData.value.slice(start, end);
});

const prevPage = () => {
  if (currentPage.value > 1) currentPage.value--;
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) currentPage.value++;
};

// 🛡️ UNCONDITIONAL PLATFORM AUTHORITY OVERRIDE
const canManageOptions = computed(() => {
  return userStore.role === 'super-admin' || 
         userStore.isAdmin || 
         userStore.permissions?.canViewEngineering === true ||
         userStore.permissions?.canManageEngineering === true;
});

// 🛡️ DETERMINISTIC SYNTHESIS ENGINE
const triggerSynthesis = async () => {
    isAiLoading.value = true;
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    if (!cid) { alert("Identity context missing."); isAiLoading.value = false; return; }
    
    await new Promise(r => setTimeout(r, 1500));

    try {
        const totalRequests = supportRequestSubmissions.value.length;
        const pendingCount = supportRequestSubmissions.value.filter(s => s.status === 'Pending').length;
        const totalProjects = projects.value.length;
        const activeProjectsCount = projects.value.filter(p => p.status === 'Active').length;
        const inspectionCount = worksiteInspectionSubmissions.value.length;

        const completionRate = totalRequests > 0 ? Math.round(((totalRequests - pendingCount) / totalRequests) * 100) : 100;

        const report = {
            summary: `Project operational audit reports a field task resolution index of ${completionRate}%. Currently monitoring ${activeProjectsCount} active projects across ${totalProjects} total registrations. Worksite inspections show ${inspectionCount} completed audit records archived.`,
            trends: [
                { label: 'Field Queue Backlog', value: `${pendingCount} Tasks pending triage`, isCritical: pendingCount > 3 },
                { label: 'Active Project Load', value: `${activeProjectsCount} Active project deployments`, isCritical: false },
                { label: 'Site Inspection Index', value: `${inspectionCount} Inspections archived`, isCritical: false }
            ],
            actions: [
                { type: 'Resource Triage', instruction: `Authorise ${pendingCount} outstanding requests`, reason: 'Pending tasks restrict active project logistics.' },
                { type: 'Site Audit', instruction: `Review ${inspectionCount} worksite inspections`, reason: 'Regular verification ensures site compliance.' }
            ],
            createdAt: new Date().toISOString()
        };

        const reportRef = doc(db, 'clients', cid, 'engineering_intelligence', 'latest');
        await setDoc(reportRef, { ...report, clientId: cid }, { merge: true });
        aiReport.value = report;

    } catch (e) {
        console.error("Synthesis Protocol Failure:", e);
        alert("Registry Handshake Failure: Insufficient authorisation to save intelligence log.");
    } finally {
        getAnalyticsDataFallback();
        isAiLoading.value = false;
    }
};

const fetchLatestAiReport = async (cid) => {
  try {
    const docSnap = await getDoc(doc(db, 'clients', cid, 'engineering_intelligence', 'latest'));
    if (docSnap.exists()) {
      aiReport.value = docSnap.data();
    }
  } catch (err) {
    console.warn("Engineering Intelligence Registry Neutral.");
  }
};

const getAnalyticsDataFallback = async () => {
  try {
    const result = await httpsCallable(functions, 'getAnalyticsData')();
    analyticsData.value = result.data || {};
  } catch (err) {
    console.warn("Direct analytics sync failed or bypassed for Super Admin:", err);
  }
};

const setupListeners = async (clientId) => {
  loading.value = true;
  await fetchLatestAiReport(clientId);
  
  onSnapshot(query(collection(db, 'clients', clientId, 'projects')), (snap) => {
    projects.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  });

  onSnapshot(query(collection(db, 'supportRequestTemplates'), where('clientId', '==', clientId)), (snap) => {
    supportRequestTemplates.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  });

  // Submissions Listener
  onSnapshot(query(collection(db, 'healthsafetyFormSubmissions'), where('clientId', '==', clientId)), (snap) => {
    const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    
    clientFeedbackSubmissions.value = raw
      .filter(d => d.formIdentifier === 'clientFeedbackForm')
      .sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB - dateA;
      });

    singleItemInspectionSubmissions.value = raw
      .filter(d => d.formIdentifier === 'singleItemInspectionForm')
      .sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB - dateA;
      });

    multiItemInspectionSubmissions.value = raw
      .filter(d => d.formIdentifier === 'multiItemInspectionForm')
      .sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB - dateA;
      });

    worksiteInspectionSubmissions.value = raw
      .filter(d => d.formIdentifier === 'worksiteInspectionForm')
      .sort((a, b) => {
        const dateA = a.submittedAt?.toDate ? a.submittedAt.toDate() : new Date(a.submittedAt || 0);
        const dateB = b.submittedAt?.toDate ? b.submittedAt.toDate() : new Date(b.submittedAt || 0);
        return dateB - dateA;
      });
  });

  watchEffect(() => {
    let q = query(collection(db, 'supportRequestSubmissions'), where('clientId', '==', clientId), orderBy('submittedAt', 'desc'), limit(rowsPerPage));
    onSnapshot(q, async (snap) => {
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      const processed = await Promise.all(raw.map(async (docData) => {
        let resolvedName = docData.requestorName || docData.formData?.requestorName;
        if (!resolvedName) resolvedName = docData.userName || docData.formData?.userName;

        if (!resolvedName || resolvedName.toUpperCase() === 'GUEST') {
           const uid = docData.userId || docData.submittedBy || docData.formData?.userId;
           if (uid) {
              const pSnap = await getDoc(doc(db, 'userProfiles', uid));
              if (pSnap.exists()) {
                 const p = pSnap.data();
                 resolvedName = `${p.firstName} ${p.lastName}`;
              }
           }
        }
        if (!resolvedName || resolvedName.toUpperCase() === 'GUEST') resolvedName = 'Field Staff';

        const url = `${window.location.origin}/completed-form/supportRequestSubmissions/${docData.id}`;
        if (!qrCodeUrls.value[docData.id]) {
           try { qrCodeUrls.value[docData.id] = await QRCode.toDataURL(url, { width: 100, margin: 1 }); } catch (e) { console.error(e); }
        }
        return { ...docData, userName: resolvedName };
      }));
      
      supportRequestSubmissions.value = processed;
      loading.value = false;
    });
  });
};

const fetchData = () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  if (cid) {
    setupListeners(cid);
    getAnalyticsDataFallback();
  }
};

const statusClass = (status) => {
  const s = status || 'Pending Review';
  if (s === 'Reviewed' || s === 'Completed') return 'bg-[#EBFDF8] text-[#20FCF1] border border-[#20FCF1]/10';
  if (s === 'Requires Action') return 'bg-rose-50 text-rose-600 border-rose-100';
  return 'bg-amber-50 text-amber-600 border-amber-100';
};

const getStatusClass = (status) => {
  if (status === 'Active') return 'bg-[#EBFDF8] text-[#20FCF1] border border-[#20FCF1]/10';
  return 'bg-slate-100 text-slate-500 border-slate-200';
};

const formatDate = (val) => {
  if (!val) return 'N/A';
  const date = val.toDate ? val.toDate() : new Date(val);
  return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const openCreateModal = () => { editingTemplate.value = null; isModalOpen.value = true; };
const openEditModal = (template) => { editingTemplate.value = template; isModalOpen.value = true; };
const closeModal = () => { isModalOpen.value = false; editingTemplate.value = null; };

const shareForm = (form) => {
  const fullUrl = `${window.location.origin}/support-request/${form.id}`;
  navigator.clipboard.writeText(fullUrl).then(() => {
    copiedSlug.value = form.id;
    setTimeout(() => { copiedSlug.value = null; }, 2500);
  });
};

const openGlobalClientFeedbackModal = async () => {
  isClientFeedbackModalOpen.value = true;
  try {
    const cid = userStore.clientId || userStore.userProfile?.clientId;
    const feedbackUrl = `${window.location.origin}/public/client-feedback/${cid || 'general'}`;
    globalClientFeedbackQrUrl.value = await QRCode.toDataURL(feedbackUrl, { width: 300, margin: 1 });
  } catch (err) {
    console.error("QR Generation Failure:", err);
  }
};

const shareGlobalFeedbackEmail = () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${cid || 'general'}`;
  const subject = encodeURIComponent("Client Feedback Questionnaire");
  const body = encodeURIComponent("Hello,\n\nPlease take a moment to complete our client feedback questionnaire regarding our worksite performance using the link below:\n\n" + feedbackUrl + "\n\nThank you.");
  window.location.href = "mailto:?subject=" + subject + "&body=" + body;
};

const copyGlobalFeedbackLink = () => {
  const cid = userStore.clientId || userStore.userProfile?.clientId;
  const feedbackUrl = `${window.location.origin}/public/client-feedback/${cid || 'general'}`;
  navigator.clipboard.writeText(feedbackUrl).then(() => {
    copiedGlobalFeedbackText.value = 'Copied to Clipboard!';
    setTimeout(() => { copiedGlobalFeedbackText.value = 'Copy Questionnaire Link'; }, 2500);
  });
};

const exportToExcel = () => {
  exporting.value = true;
  try {
    const data = currentViewData.value.map(item => ({
      'Reference': item.humanReadableId || item.formData?.humanReadableId || item.id,
      'Identity': item.userName || item.name || item.companyDisplayName || item.formData?.clientName || item.itemDetails?.assetId || item.formData?.inspectorName,
      'Status / Rating': item.status || item.formData?.overallRating || 'N/A',
      'Date': formatDate(item.submittedAt || item.createdAt)
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registry");
    XLSX.writeFile(wb, `Engineering_Export_${activeTab.value}.xlsx`);
  } catch (err) { alert(err.message); } finally { exporting.value = false; }
};

const deleteProject = async (p) => { if (confirm(`Delete project ${p.name}?`)) await httpsCallable(functions, 'manageProject')({ action: 'delete', projectId: p.id }); };
const handleDelete = async (t) => { if (confirm(`Delete template ${t.companyDisplayName}?`)) await httpsCallable(functions, 'deleteFormTemplate')({ templateId: t.id }); };

onMounted(() => { 
  if (route.query.tab) {
    activeTab.value = route.query.tab;
  }
  if (route.query.search) {
    filters.search = route.query.search;
  }

  if (userStore.isAuthReady) fetchData(); 
});

watch([activeTab, () => filters.search, () => filters.status, itemsPerPage], () => {
  currentPage.value = 1;
});
</script>

<style scoped>
.filter-input-dark { @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-sm font-bold text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all placeholder-slate-500; }
.filter-select-dark {
  @apply w-full bg-[#1E293B] border border-white/10 rounded-xl px-5 py-3 text-[10px] font-black uppercase tracking-widest text-white focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all cursor-pointer appearance-none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 24 24\' stroke=\'%2338E3B0\'%3E%3Cpath stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M19 9l-7 7-7-7\'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1rem;
}
.action-btn { @apply p-3 rounded-2xl bg-slate-50 text-slate-400 transition-all flex items-center justify-center shadow-sm; }
.animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
</style>